import type { Dashboard } from "@/data/dashboards";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { formatZodError } from "@Simplicity-Education/core";
import { sonnerToast } from "@simplicity/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// SIM-1769 — JSON.stringify on `{ x: undefined }` drops the key, but empty
// strings get sent verbatim. The server's PATCH route validates url with
// `z.string().url().optional()`, which rejects `""`. SparkDashboards have
// `url: ""` and the Edit dialog always forwards that value, so saving a
// SparkDashboard rename silently 400s. Normalize empty strings to undefined
// here so the field is omitted from the body and the optional schema kicks in.
function omitEmpty(value: string | undefined): string | undefined {
  return value === "" ? undefined : value;
}

function reportSaveError(err: unknown): void {
  sonnerToast.error("Save failed", {
    description: formatZodError(err, { unknownFallback: "Save failed" }),
  });
}

export type ApiDashboard = {
  id: string;
  tenantId: string;
  title: string;
  slug: string;
  url: string;
  category: string;
  schoolYear: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  lastViewedAt?: string | null;
  viewCount?: number;
  type?: "looker" | "sheets" | "spark";
  sparkDashboardId?: string | null;
  tags?: Array<{ propertyKey: string; value: string }>;
};

export function adapt(d: ApiDashboard): Dashboard {
  return {
    id: d.id,
    tenantId: d.tenantId,
    tenant_id: d.tenantId,
    title: d.title,
    slug: d.slug,
    url: d.url,
    category: d.category,
    school_year: d.schoolYear,
    archived: d.archived,
    created_at: d.createdAt,
    updated_at: d.updatedAt,
    lastViewedAt: d.lastViewedAt,
    viewCount: d.viewCount,
    type: d.type ?? "looker",
    sparkDashboardId: d.sparkDashboardId ?? null,
    tags: d.tags ?? [],
  };
}

export function useDashboards() {
  const { currentTenantId } = useTenant();
  // SIM-3117: ask the server what this user may see rather than re-deriving it
  // from a role string. The server gates `/dashboards/all` on the same named
  // capability, so the two cannot disagree about which endpoint is legal.
  const { canViewAllDashboards } = useCapabilities();

  const path = canViewAllDashboards
    ? `/api/portal/tenants/${currentTenantId}/dashboards/all`
    : `/api/portal/tenants/${currentTenantId}/dashboards`;

  const { data, isLoading } = useQuery({
    queryKey: ["portal-dashboards", currentTenantId, canViewAllDashboards],
    queryFn: () => portalFetch<ApiDashboard[]>(path).then((items) => items.map(adapt)),
    enabled: !!currentTenantId,
  });

  return { data: data ?? [], isLoading };
}

export function useArchiveDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/dashboards/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, id, archived }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-dashboards"] }),
  });
}

export function useDeleteDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/dashboards/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-dashboards"] }),
  });
}

export function useAddDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (dashboard: {
      title: string;
      url: string;
      school_year: string;
      category: string;
      groupIds?: string[];
    }) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/dashboards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId: currentTenantId,
          title: dashboard.title,
          url: dashboard.url,
          schoolYear: dashboard.school_year,
          category: dashboard.category,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-dashboards"] }),
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

/**
 * SIM-1418 — replace the FULL desired tag set for a dashboard. Server diffs
 * against existing rows and emits minimal inserts/deletes; client just
 * invalidates the list query on success so card displays and filter
 * counts reflect the new tagging.
 */
export function useSetDashboardTags() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      tags,
    }: {
      dashboardId: string;
      tags: Array<{ propertyKey: string; value: string }>;
    }) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/dashboards/${dashboardId}/tags`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, dashboardId, tags }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-dashboards"] }),
  });
}

export function useUpdateDashboard() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (update: {
      id: string;
      title?: string;
      url?: string;
      school_year?: string;
      category?: string;
    }) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/dashboards/${update.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId: currentTenantId,
          id: update.id,
          title: omitEmpty(update.title),
          url: omitEmpty(update.url),
          schoolYear: omitEmpty(update.school_year),
          category: omitEmpty(update.category),
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-dashboards"] }),
    onError: reportSaveError,
  });
}
